import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Navigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import KeyOutlinedIcon from "@mui/icons-material/KeyOutlined";
import SearchIcon from "@mui/icons-material/Search";
import { getTenants } from "../api/tenantApi";
import { listEdges } from "../api/edgeApi";
import { formatDate } from "../utils/i18nFormat";
import { getTenantLicenses, updateEdgeLicense, updateTenantLicenses } from "../api/licenseAdminApi";

const PAGE_SIZE = 50;

function localDateValue(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function dateInputValue(value) {
  if (!value) return "";
  return localDateValue(new Date(value));
}

function localDayIso(value, endOfDay = false) {
  return new Date(`${value}T${endOfDay ? "23:59:59.999" : "00:00:00"}`).toISOString();
}

function plusDays(value, days) {
  const date = new Date(`${value}T12:00:00`);
  date.setDate(date.getDate() + days);
  return localDateValue(date);
}

function licenseState(license) {
  if (!license?.license_expires_at) return "unlicensed";
  const now = Date.now();
  if (license.license_starts_at && new Date(license.license_starts_at).getTime() > now) return "scheduled";
  if (new Date(license.license_expires_at).getTime() < now) return "expired";
  return "active";
}

const stateMeta = {
  active: { color: "success" },
  scheduled: { color: "info" },
  expired: { color: "error" },
  unlicensed: { color: "default" },
};

function displayDate(value) {
  return formatDate(value, { dateStyle: "medium" });
}

function DateRangeDialog({ open, target, initial, busy, onClose, onSubmit, onRevoke }) {
  const { t } = useTranslation();
  const today = localDateValue(new Date());
  const [start, setStart] = useState(today);
  const [end, setEnd] = useState(plusDays(today, 30));

  useEffect(() => {
    if (!open) return;
    setStart(dateInputValue(initial?.license_starts_at) || today);
    setEnd(dateInputValue(initial?.license_expires_at) || plusDays(today, 30));
  }, [open, initial, today]);

  const save = () => {
    if (!start || !end || start > end) {
      toast.warning(t("admin.licenses.dateOrder"));
      return;
    }
    onSubmit({ license_starts_at: localDayIso(start), license_expires_at: localDayIso(end, true) });
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pb: 1 }}>{t("admin.licenses.dialog.title")}</DialogTitle>
      <DialogContent>
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          {target?.kind === "tenant" ? t("admin.licenses.dialog.tenantTarget", { name: target.name }) : t("admin.licenses.dialog.edgeTarget", { name: target?.name || t("admin.licenses.dialog.edgeFallback") })}
        </Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <TextField
            label={t("admin.licenses.dialog.start")}
            type="date"
            value={start}
            onChange={(event) => setStart(event.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ "aria-label": t("admin.licenses.dialog.startAria") }}
            fullWidth
          />
          <TextField
            label={t("admin.licenses.dialog.end")}
            type="date"
            value={end}
            onChange={(event) => setEnd(event.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ "aria-label": t("admin.licenses.dialog.endAria") }}
            fullWidth
          />
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.25 }}>
          {t("admin.licenses.dialog.hint")}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {target?.kind === "edge" && initial?.license_expires_at && (
          <Button color="error" onClick={onRevoke} disabled={busy} sx={{ mr: "auto" }}>{t("admin.licenses.dialog.revoke")}</Button>
        )}
        <Button onClick={onClose} disabled={busy}>{t("admin.licenses.dialog.cancel")}</Button>
        <Button variant="contained" onClick={save} disabled={busy} startIcon={busy ? <CircularProgress size={16} /> : <KeyOutlinedIcon />}>
          {target?.kind === "tenant" ? t("admin.licenses.dialog.submitTenant") : t("admin.licenses.dialog.submitEdge")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function LicenseManagement() {
  const { t } = useTranslation();
  const accessToken = useSelector((state) => state.session?.accessToken);
  const me = useSelector((state) => state.session?.me);
  const [tenants, setTenants] = useState([]);
  const [tenantId, setTenantId] = useState("");
  const [edges, setEdges] = useState([]);
  const [licenseByEdge, setLicenseByEdge] = useState({});
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loadingTenants, setLoadingTenants] = useState(true);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [dialogTarget, setDialogTarget] = useState(null);
  const [dialogLicense, setDialogLicense] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(0);
      setSearch(searchInput.trim());
    }, 250);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    if (!accessToken) return;
    let cancelled = false;
    setLoadingTenants(true);
    getTenants(accessToken, { limit: 200, offset: 0 })
      .then((result) => {
        if (cancelled) return;
        const items = result.items || result || [];
        setTenants(items);
        setTenantId((current) => current || items[0]?.id || "");
      })
      .catch((err) => {
        if (!cancelled) toast.error(err.response?.data?.detail || t("admin.licenses.tenantsFailed"));
      })
      .finally(() => { if (!cancelled) setLoadingTenants(false); });
    return () => { cancelled = true; };
  }, [accessToken, t]);

  const refresh = useCallback(async () => {
    if (!accessToken || !tenantId) return;
    setLoading(true);
    try {
      const [edgeResult, licenseResult] = await Promise.all([
        listEdges(accessToken, { tenant_id: tenantId, q: search || undefined, limit: PAGE_SIZE, offset: page * PAGE_SIZE }),
        getTenantLicenses(accessToken, tenantId),
      ]);
      setEdges(edgeResult.items || []);
      setTotal(edgeResult.total || 0);
      setLicenseByEdge(Object.fromEntries((licenseResult.edges || []).map((item) => [item.edge_id, item])));
    } catch (err) {
      toast.error(err.response?.data?.detail || t("admin.licenses.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [accessToken, tenantId, search, page, t]);

  useEffect(() => { refresh(); }, [refresh]);

  const selectedTenant = tenants.find((tenant) => tenant.id === tenantId);
  const licenseCounts = useMemo(() => {
    const counts = { active: 0, scheduled: 0, expired: 0, unlicensed: 0 };
    edges.forEach((edge) => { counts[licenseState(licenseByEdge[edge.id])] += 1; });
    return counts;
  }, [edges, licenseByEdge]);

  const visibleEdges = edges.filter((edge) => statusFilter === "all" || licenseState(licenseByEdge[edge.id]) === statusFilter);

  const openEdgeDialog = (edge) => {
    setDialogTarget({ kind: "edge", id: edge.id, name: edge.name || edge.edgeId });
    setDialogLicense(licenseByEdge[edge.id] || null);
  };

  const openTenantDialog = () => {
    setDialogTarget({ kind: "tenant", id: tenantId, name: selectedTenant?.name || t("admin.licenses.tenantFallback") });
    setDialogLicense(null);
  };

  const closeDialog = () => { if (!busy) setDialogTarget(null); };

  const submitRange = async (range) => {
    if (dialogTarget?.kind === "tenant" && !window.confirm(t("admin.licenses.confirmTenant", { name: selectedTenant?.name }))) return;
    setBusy(true);
    try {
      const result = dialogTarget.kind === "tenant"
        ? await updateTenantLicenses(accessToken, dialogTarget.id, range)
        : await updateEdgeLicense(accessToken, dialogTarget.id, range);
      const updatedEdges = dialogTarget.kind === "tenant" ? result.edges : [result];
      const expectedStart = new Date(range.license_starts_at).getTime();
      if (!updatedEdges?.length || updatedEdges.some((edge) => new Date(edge.license_starts_at).getTime() !== expectedStart)) {
        throw new Error(t("admin.licenses.notVerified"));
      }
      toast.success(dialogTarget.kind === "tenant" ? t("admin.licenses.tenantUpdated") : t("admin.licenses.edgeSaved"));
      setDialogTarget(null);
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || t("admin.licenses.assignFailed"));
    } finally {
      setBusy(false);
    }
  };

  const revokeLicense = async () => {
    if (!dialogTarget || !window.confirm(t("admin.licenses.confirmRevoke", { name: dialogTarget.name }))) return;
    setBusy(true);
    try {
      await updateEdgeLicense(accessToken, dialogTarget.id, { license_expires_at: null });
      toast.success(t("admin.licenses.revoked"));
      setDialogTarget(null);
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("admin.licenses.revokeFailed"));
    } finally {
      setBusy(false);
    }
  };

  if (!me) return <Box sx={{ p: 4 }}><CircularProgress size={24} /></Box>;
  if (me.global_role !== "superadmin") return <Navigate to="/home" replace />;

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1500, mx: "auto" }}>
      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ md: "flex-end" }} spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="overline" color="primary" sx={{ letterSpacing: ".16em", fontWeight: 800 }}>VALONIX · CONTROL PLANE</Typography>
          <Typography variant="h4" sx={{ fontWeight: 750, letterSpacing: "-.035em" }}>{t("admin.licenses.title")}</Typography>
          <Typography color="text.secondary" sx={{ mt: .5 }}>{t("admin.licenses.subtitle")}</Typography>
        </Box>
        <Button variant="contained" startIcon={<KeyOutlinedIcon />} onClick={openTenantDialog} disabled={!tenantId || loadingTenants}>
          {t("admin.licenses.assignTenant")}
        </Button>
      </Stack>


      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" }, gap: 1.5, mb: 2.5 }}>
        {[["active", licenseCounts.active, "#15803d"], ["scheduled", licenseCounts.scheduled, "#0369a1"], ["expired", licenseCounts.expired, "#b91c1c"], ["unlicensed", licenseCounts.unlicensed, "#64748b"]].map(([state, value, color]) => (
          <Paper key={state} variant="outlined" sx={{ p: 2, borderTop: `3px solid ${color}` }}>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: ".08em", fontWeight: 700 }}>{t("admin.licenses.thisPage", { label: t(`admin.licenses.state.${state}`) })}</Typography>
            <Typography variant="h4" sx={{ mt: .25, fontWeight: 750 }}>{value}</Typography>
          </Paper>
        ))}
      </Box>

      <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
          <TextField select size="small" label={t("admin.licenses.tenant")} value={tenantId} onChange={(event) => { setTenantId(event.target.value); setPage(0); }} sx={{ minWidth: { md: 260 } }} disabled={loadingTenants}>
            {tenants.map((tenant) => <MenuItem key={tenant.id} value={tenant.id}>{tenant.name}</MenuItem>)}
          </TextField>
          <TextField size="small" label={t("admin.licenses.searchDevice")} value={searchInput} onChange={(event) => setSearchInput(event.target.value)} InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: "text.secondary" }} /> }} sx={{ flex: 1 }} />
          <TextField select size="small" label={t("admin.licenses.statusFilter")} value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)} sx={{ minWidth: 180 }}>
            <MenuItem value="all">{t("admin.licenses.allStates")}</MenuItem>
            {Object.keys(stateMeta).map((state) => <MenuItem key={state} value={state}>{t(`admin.licenses.state.${state}`)}</MenuItem>)}
          </TextField>
        </Stack>
      </Paper>

      <TableContainer component={Paper} variant="outlined">
        <Table size="medium" aria-label={t("admin.licenses.tableAria")}>
          <TableHead><TableRow sx={{ bgcolor: "action.hover" }}>
            <TableCell>{t("admin.licenses.col.device")}</TableCell><TableCell>{t("admin.licenses.col.state")}</TableCell><TableCell>{t("admin.licenses.col.start")}</TableCell><TableCell>{t("admin.licenses.col.end")}</TableCell><TableCell align="right">{t("admin.licenses.col.action")}</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={5} align="center" sx={{ py: 5 }}><CircularProgress size={26} /></TableCell></TableRow>}
            {!loading && visibleEdges.map((edge) => {
              const license = licenseByEdge[edge.id];
              const status = licenseState(license);
              const meta = stateMeta[status];
              return <TableRow key={edge.id} hover>
                <TableCell><Typography fontWeight={700}>{edge.name || edge.edgeId}</Typography><Typography variant="caption" color="text.secondary">{edge.edgeId}</Typography></TableCell>
                <TableCell><Chip size="small" label={t(`admin.licenses.state.${status}`)} color={meta.color} variant={status === "unlicensed" ? "outlined" : "filled"} /></TableCell>
                <TableCell>{displayDate(license?.license_starts_at)}</TableCell>
                <TableCell>{displayDate(license?.license_expires_at)}</TableCell>
                <TableCell align="right"><Button size="small" onClick={() => openEdgeDialog(edge)}>{status === "unlicensed" ? t("admin.licenses.assign") : t("admin.licenses.edit")}</Button></TableCell>
              </TableRow>;
            })}
            {!loading && visibleEdges.length === 0 && <TableRow><TableCell colSpan={5} align="center" sx={{ py: 5, color: "text.secondary" }}>{edges.length ? t("admin.licenses.noMatch") : t("admin.licenses.noDevices")}</TableCell></TableRow>}
          </TableBody>
        </Table>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 1.25, borderTop: 1, borderColor: "divider" }}>
          <Typography variant="caption" color="text.secondary">{t("admin.licenses.paging", { total, page: page + 1 })}</Typography>
          <Stack direction="row" spacing={1}>
            <Button size="small" disabled={page === 0 || loading} onClick={() => setPage((current) => current - 1)}>{t("admin.licenses.prev")}</Button>
            <Button size="small" disabled={(page + 1) * PAGE_SIZE >= total || loading} onClick={() => setPage((current) => current + 1)}>{t("admin.licenses.next")}</Button>
          </Stack>
        </Stack>
      </TableContainer>

      <DateRangeDialog
        open={Boolean(dialogTarget)} target={dialogTarget} initial={dialogLicense} busy={busy}
        onClose={closeDialog} onSubmit={submitRange} onRevoke={revokeLicense}
      />
    </Box>
  );
}
